import dayjs from 'dayjs';
import { getDaysText } from 'utils/translations/i18n';
import configJSON from 'config.json';
import {
  RouteMatchObject,
  generateRouteMatchObjectFromPath,
  LISTING_ROUTE,
} from 'components/dynamic-page/route-matchers';
import { generateBreadcrumbs, Breadcrumb } from 'components/breadcrumbs';
import getLatestNonDeletedListing from 'utils/get-latest-no-deleted-listing';
import { capitalize, capitalizeWords } from '@zoocasa/node-kit/strings/capitalize';
import isUserAgentCrawler, { isUserAgentLighthouse } from 'utils/crawler-agent';
import generateCityFooterData from 'components/dynamic-page/area-listings-page/generate-dynamic-footer-data';
import { countryCodeFromProvinceOrState, ProvinceAndState } from 'utils/province_or_state';
import { getFlattenFilters, filterToURLSearchParams, getExtendedFilteredUrl } from 'components/dynamic-page/new-area-listings-page/targeted-url-helper';
import { getThemeOverrideFromRequest, themeNameOrDefault } from 'utils/themes';
import { ThemeConfig, themes } from 'themes';
import Listing, { getListingById, getListingsBySlug, toJson } from 'data/listing';
import { getAddressesBySlug } from 'data/addresses';
import sortAddresses from 'utils/sort-addresses';

import type { GetServerSidePropsContext } from 'next';
import type Address from 'data/addresses';
import type { IncomingMessage, ServerResponse } from 'http';
import type { LinkDataType } from 'components/home-page/internal-links';
import { getFeaturesOverrideFromRequest } from 'utils/features';
import { AbortReasons } from 'constants/abort-reasons';
import { ApiTimeouts } from 'constants/timeouts';
import { generateCanonicalUrl } from 'utils/urls/canonicalUrls';
import { getSiteLocationFromRequest } from 'utils/site-location';
import { HrefLangLink } from '../new-area-listings-page/util/head-tags-builder';

export default async function getServerSideProps({ query, res, req, locale }: GetServerSidePropsContext) {
  const featureOverrides = getFeaturesOverrideFromRequest(req);
  const features = { ...configJSON.features, ...featureOverrides };
  const siteCountry = getSiteLocationFromRequest(req);
  let listing: Listing | null = null;
  let listings: Listing[] = [];
  const path = (query.dynamic as string[]).join('/');
  const routeMatchObject: RouteMatchObject = generateRouteMatchObjectFromPath(path);
  const { listingId, listingSlug } = routeMatchObject;

  // Set up a custom API abort controller
  const controller = new AbortController();
  const abortSignal = controller.signal;
  let abortReason = null;

  // Abort the controller upon timeout
  const timeoutId = setTimeout(() => {
    abortReason = AbortReasons.TIMEOUT;
    controller.abort(abortReason);
  }, ApiTimeouts.GET_LISTING);

  try {
    if (listingId) {
      listing = await getListingById(listingId, true, abortSignal);
      if (listing.addressPath) {
        return redirectToAddressPageWithModal(res, listing.addressPath, listing.id);
      } else {
        return redirectToAreaPage(res, req, routeMatchObject);
      }
    } else if (listingSlug) {
      const addresses = await getAddressesBySlug(listingSlug, abortSignal);
      if (addresses?.length > 0) {
        const sortedAddresses = sortAddresses(addresses, false);
        const topAddress = sortedAddresses.pop();
        if (topAddress?.addressType === 'address') {
          listings = (await getListingsBySlug({ slug: listingSlug, getDeleted: true, isServerSide: true }, abortSignal)).listings;
          if (!listings.length) {
            return redirectToAreaPage(res, req, routeMatchObject);
          }
          listing = getLatestNonDeletedListing(listings) as Listing;
        } else {
          return redirectToAreaPage(res, req, routeMatchObject);
        }
      }
    }

    if (listing === null) {
      return redirectToAreaPage(res, req, routeMatchObject);
    }

    const addressSlug = (listing as Record<string, any>)?.attributes['address-slug'];
    const addressPath = (listing as Record<string, any>)?.attributes['address-path'] || listing?.path;
    const addressProvince = (listing as Record<string, any>)?.attributes['province'];

    if (('/' + path) !== addressPath) {
      res.writeHead(301, { Location: addressPath }).end();
      return {};
    }

    const addresses = addressSlug ? await getAddressesBySlug(addressSlug) : [];
    const addressesData = addresses.map(address => toJson(address));
    const country = countryCodeFromProvinceOrState(addressProvince || '');
    const breadcrumbs = generateBreadcrumbs(country, addresses, addressPath);
    const isCrawler = isUserAgentCrawler(req.headers);
    const isCrawlerLighthouse = isUserAgentLighthouse(req.headers['user-agent']);
    let footerData: LinkDataType[] = [];
    const themeName = getThemeOverrideFromRequest(req);
    const theme = themes[themeNameOrDefault(themeName)];
    if (isCrawler) {
      footerData = await getFooterData(addressesData, themeName, features.useLegacySearchFilter || false) || [];
    }
    const canonicalUrl = generateCanonicalUrl(addressPath, theme.schemaUrl);
    const hrefLangs = [{ href: canonicalUrl, hrefLang: `${locale}-ca`.toLowerCase() }, { href: canonicalUrl, hrefLang: `${locale}-us`.toLowerCase() }];

    return {
      props: {
        listingData: toJson(listing),
        listingsData: listings.map(listing => toJson(listing)),
        breadcrumbs,
        headTags: updateHeadTags(theme, new Listing(listing as unknown as Record<string, unknown>), breadcrumbs, listingId, hrefLangs),
        isCrawler,
        isCrawlerLighthouse,
        footerData: footerData,
        addressesData: addressesData,
      },
      routeName: LISTING_ROUTE,
    };
  } catch (err) {
    if (abortSignal.aborted) {
      return {
        props: {
          errorReason: abortReason,
        },
      };
    }
    return {
      props: {
        errorReason: (err as { message: string})?.message,
      },
    };
  } finally {
    clearTimeout(timeoutId);
  }
}

export async function getFooterData(params: Address[], tenant, useLegacySearchFilter = false) {
  const city = params.filter(address => address.addressType == 'sub-division').pop();
  if (city) {
    const slug = city.slug;
    const area = city.subDivision || '';
    const province = city.province.toUpperCase() as keyof typeof ProvinceAndState;
    const data = await generateCityFooterData(area, slug, tenant, province, 10, useLegacySearchFilter, false);
    return data;
  }
}

function redirectToAddressPageWithModal(res: ServerResponse, path: string, listingId: number) {
  const redirectPath = `${path}#${listingId}`;
  res.writeHead(301, { Location: redirectPath }).end();
  return {};
}

function redirectToAreaPage(res: ServerResponse, req: IncomingMessage, routeMatchObject: RouteMatchObject) {
  const themeName = getThemeOverrideFromRequest(req);
  const host = themes[themeNameOrDefault(themeName)].schemaUrl;
  const addressPath = req.url!.replace(/\/[^/]+$/, '').replace('/_next/data/development/address', '');

  // If the subLocation includes a number, and secondary filter includes a number redirect with filters attached
  // This is because the router mistakenly thought this was an address but really its an area with a secondary filter
  // ex; /north-las-vegas-nv-real-estate/shadows-unit-4/2-bedroom
  if ((/\d/.test(routeMatchObject?.street) || /\d/.test(routeMatchObject?.neighbourhood)) && /\d/.test(routeMatchObject?.secondaryFilter)) {
    const flatFilters = getFlattenFilters(routeMatchObject?.filter);
    delete flatFilters.slug;
    delete flatFilters.areaName;
    delete flatFilters.latitude;
    delete flatFilters.longitude;
    delete flatFilters.zoom;
    delete flatFilters.boundary;
    const filters = filterToURLSearchParams(flatFilters);
    const path = getExtendedFilteredUrl(host + addressPath, filters);
    res.writeHead(301, { Location: path.href }).end();
    return {};
  }

  res.writeHead(301, { Location: addressPath }).end();
  return {};
}

export function getListingPageTitle(theme: ThemeConfig, statusLabel: string | null, seoStreet: string | null, city: string | null) {
  return `${seoStreet}, ${statusLabel} in ${city} - ${theme.name}`;
}

export function getListingPageDescription(isAvailable: boolean, isOffMarket: boolean, isRental: boolean | null, bedrooms: number, address: string | null, city: string | null, priceLabel?: string, home?: string | null) {
  let metaDescription;
  if (!home) {
    home = 'Real Estate';
  }

  if (home.includes('house-')) {
    home = 'house';
  }
  if (isAvailable) {
    metaDescription = `${bedrooms} Bedroom ${capitalizeWords(home)} has been listed for ${isRental ? 'rent' : 'sale'} at ${address} in ${city} for ${priceLabel}. Chat with a local expert, book a tour.`;
  } else if (isOffMarket) {
    metaDescription = `Browse photos, facts and stats for real estate listings near ${address}, ${city}.`;
  } else {
    metaDescription = `${bedrooms} Bedroom ${capitalizeWords(home)} at ${address} has been ${isRental ? 'rented' : 'sold'} for ${priceLabel}. Click here to view details, map, and photos.`;
  }
  return metaDescription;
}

export function updateHeadTags(theme: ThemeConfig, listing: Listing, breadcrumbs: Breadcrumb[], listingId: number, hrefLangs?: HrefLangLink[]) {
  const { isAvailable, statusLabel, priceLabel, isRental, seoStreet, city, province, price, bedrooms, postalCode, isPrivate,
    mlsNum, imageUrl, path, addressPath, position, virtualTourUrl, addedAt,
    brokerage, homeType, sqftLabel, bathrooms, parking, taxes, daysOnMarket } = listing;

  const listingImage = imageUrl || `${theme.schemaUrl}/next/assets/images/banner.png`;
  const isOffMarket = listing.isOffMarket;
  const pricePrivacyLabel = isPrivate ? priceLabel?.replace(/[0-9]/g, 'X') : priceLabel;

  const title = getListingPageTitle(theme, statusLabel, seoStreet, city);
  const metaDescription = getListingPageDescription(isAvailable, isOffMarket, isRental, bedrooms, seoStreet, city, pricePrivacyLabel, homeType);
  const isFAQMarkupEnabled = configJSON.features.useFAQMarkUp;

  const offers = {
    '@type': 'Offer',
    price: price,
    priceCurrency: 'CAD',
    url: `${theme.schemaUrl}${addressPath}`,
  };

  const eventSchema = {
    '@context': 'https://schema.org',
    '@type': 'Event',
    description: `Take a 3D tour of ${seoStreet}`,
    name: 'Virtual Tour Available',
    image: listingImage,
    offers: { ...offers, validFrom: dayjs(addedAt).toString() },
    startDate: dayjs(addedAt).toString(),
    endDate: dayjs().endOf('month').toString(),
    eventAttendanceMode: 'https://schema.org/OnlineEventAttendanceMode',
    eventStatus: 'https://schema.org/EventScheduled',
    url: `${theme.schemaUrl}${addressPath}`,
    location: [{
      '@type': 'VirtualLocation',
      url: virtualTourUrl,
    }],
    organizer: {
      name: 'Zoocasa',
      url: `${theme.schemaUrl}${addressPath}`,
    },
    performer: 'Zoocasa Agent',
  };

  const breadcrumbItemList = breadcrumbs.map(({ name, link }, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    item: {
      '@id': link,
      name,
    },
  }));

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbItemList,
  };

  const residenceSchema = {
    '@context': 'https://schema.org',
    '@type': 'Residence',
    address: {
      '@type': 'PostalAddress',
      streetAddress: seoStreet,
      addressLocality: city,
      addressRegion: province,
      postalCode: postalCode,
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: position ? position.latitude.toString() : '',
      longitude: position ? position.longitude.toString() : '',
    },
  };

  const productSchema = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    description: metaDescription,
    name: title,
    image: listingImage,
    sku: mlsNum,
    offers: {
      ...offers,
      priceValidUntil: dayjs().endOf('month').toString(),
    },
    brand: brokerage,
  };

  const listingFaqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [(sqftLabel.length > 1 ? {
      '@type': 'Question',
      name: `What is the square footage of this ${homeType}?`,
      acceptedAnswer: {
        '@type': 'Answer',
        text: `${seoStreet}, ${city}, MLS#${mlsNum} has a size of ${sqftLabel}ft`,
      }} : {}),
    {
      '@type': 'Question',
      name: 'How many bedrooms and bathrooms does this home have?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: `There are ${bedrooms} bedrooms and ${bathrooms} bathrooms in this ${homeType} at ${seoStreet}, ${city}, MLS#${mlsNum}`,
      },
    },
    {
      '@type': 'Question',
      name: `Does this ${homeType} have parking available?`,
      acceptedAnswer: {
        '@type': 'Answer',
        text: parking ? `Yes, there is parking available for the ${homeType} at ${seoStreet}, ${city}, with ${parking} spot(s).` : `No, there isn’t parking available for the ${homeType} at ${seoStreet}, ${city}`,
      },
    },
    {
      '@type': 'Question',
      name: `What are annual taxes for ${seoStreet}, ${city}, ${province}?`,
      acceptedAnswer: {
        '@type': 'Answer',
        text: taxes ? `Annual taxes for this home are estimated at $${taxes} per year.` : 'No information available.',
      },
    },
    {
      '@type': 'Question',
      name: 'How long has this home been on the market?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: `${seoStreet}, ${city}, MLS#${mlsNum} has been on ${theme.name} for ${getDaysText(daysOnMarket)}.`,
      },
    },
    ],
  };
  const finalSchema : Record<string, unknown>[] = [residenceSchema];
  if (!isPrivate || isOffMarket) {
    finalSchema.push(breadcrumbSchema);
  }
  if (!isOffMarket) {
    finalSchema.push(productSchema);
    listingFaqSchema.mainEntity = listingFaqSchema.mainEntity.filter(value => Object.keys(value).length !== 0);

    if (isFAQMarkupEnabled){
      finalSchema.push(listingFaqSchema);
    }

    if (virtualTourUrl) {
      finalSchema.push(eventSchema);
    }
  }

  return {
    title:              title,
    metaDescription:    metaDescription,
    canonicalUrl:       generateCanonicalUrl(addressPath, theme.schemaUrl),
    placePostalCode:    postalCode,
    placeStreetAddress: seoStreet,
    placeLocality:      city,
    placeRegion:        province,
    ogImage:            listingImage,
    ogType:             'place',
    ogUrl:              listingId ? `${theme.schemaUrl}${path}` : `${theme.schemaUrl}${addressPath}`,
    noIndex:            listingId ? true : false,
    schema: finalSchema,
    hrefLangs,
  };
}